import { styled } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { NavItem } from '@qpr/components/Expandable/nav-items-view-model';
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { checkIfExternal } from '../../../utils/url-utils';
import Dropdown from './MegaMenuDropdown';

const PREFIX = 'MegaMenu';

const classes = {
  indicator: `${PREFIX}-indicator`,
  flexContainer: `${PREFIX}-flexContainer`,
  root: `${PREFIX}-root`,
  qprSelected: `${PREFIX}-qprSelected`
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.indicator}`]: {
    display: 'none'
  },

  [`& .${classes.flexContainer}`]: {
    maxWidth: '1000px',
    margin: 'auto'
  },

  [`& .${classes.root}`]: {
    maxWidth: '1100px',
    margin: 'auto'
  },

  [`& .${classes.qprSelected}`]: {
    paddingBottom: '10px',
    borderBottom: `2px solid ${theme.palette.primary.contrastText}`
  }
}));

interface MegaMenuProps {
  megaData: NavItem[];
  tenantSymbol: string;
  inited: boolean;
  anchorElement?: React.MutableRefObject<null>;
}

const MegaMenu = ({ anchorElement, megaData = [], tenantSymbol, inited }: MegaMenuProps) => {
  let timer: number;

  const defaultAnchor = useRef(null);

  const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
  const [dropdownContentId, setdropdownContentId] = React.useState<string | false>(false);
  const [dropdownTitle, setDropdownTitle] = React.useState('');
  const [dropdownDescription, setDropdownDescription] = React.useState<string>('');

  const handleMenuOpen = (event: React.MouseEvent) => {
    window.clearTimeout(timer);

    if (!(event.currentTarget.id === `popper${dropdownContentId}`)) {
      setdropdownContentId(event.currentTarget.id);

      const found = megaData.find(
        content => (content._id ? content._id.toString() : '') === event.currentTarget.id
      );
      setDropdownDescription(found && found.description ? found.description : '');
      setDropdownTitle(found ? found.title : '');
      if (found && found.children.length > 0) {
        setAnchorEl(anchorElement ? anchorElement.current : defaultAnchor.current);
      } else {
        setAnchorEl(null);
      }
    }
  };

  const handleMenuClose = () => {
    timer = window.setTimeout(() => {
      setAnchorEl(null);
      setdropdownContentId(false);
    }, 250);
  };

  const componentSelector = (link: string): React.ElementType<HTMLElement> | typeof Link => {
    if (link === '') return 'span' as unknown as React.ElementType<HTMLElement>;
    return checkIfExternal(link) ? ('a' as unknown as React.ElementType<HTMLElement>) : Link;
  };

  if (inited && megaData.length > 0)
    return (
      <Root>
        <Tabs
          classes={{
            indicator: classes.indicator,
            flexContainer: classes.flexContainer,
            root: classes.root
          }}
          value={dropdownContentId}
          aria-label="navigation tabs"
          data-testid="megamenu-tabs"
          scrollButtons
          variant="scrollable"
          ref={defaultAnchor}
          allowScrollButtonsMobile
          textColor="inherit"
        >
          {megaData.map((section, index) => (
            <Tab
              sx={{
                minWidth: {
                  sm: 160
                }
              }}
              key={section._id ? section._id.toString() : index}
              value={section._id && section._id.toString()}
              label={section.title}
              id={section._id ? section._id.toString() : undefined}
              data-testid="megamenu-tab"
              onMouseEnter={section.enabled ? handleMenuOpen : undefined}
              onClick={section.enabled ? handleMenuClose : undefined}
              onMouseLeave={handleMenuClose}
              disabled={!section.enabled}
              component={componentSelector(section.link)}
              to={checkIfExternal(section.link) ? '' : `/${tenantSymbol}${section.link}`}
              href={section.link}
              target={checkIfExternal(section.link) ? '_blank' : '_self'}
              classes={{
                selected: classes.qprSelected
              }}
            />
          ))}
        </Tabs>

        <Dropdown
          handleMenuClose={handleMenuClose}
          handleMenuOpen={handleMenuOpen}
          anchorEl={anchorEl}
          dropdownContentId={dropdownContentId}
          tenantSymbol={tenantSymbol}
          megaData={megaData}
          selectedTitle={dropdownTitle}
          selectedDescription={dropdownDescription}
        />
      </Root>
    );

  return null;
};

export default MegaMenu;
